<script setup>
import { RouterLink } from 'vue-router'
import { ROUTES } from '../../core/config/constants.js'
import { ref, onMounted, onUnmounted } from 'vue'

// Mouse tracking for parallax effect
const mouseX = ref(0)
const mouseY = ref(0)

const handleMouseMove = (e) => {
  const { clientX, clientY } = e
  const { innerWidth, innerHeight } = window
  mouseX.value = (clientX / innerWidth - 0.5) * 20
  mouseY.value = (clientY / innerHeight - 0.5) * 20
}

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove)
})

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove)
})
</script>

<template>
  <div class="not-found">
    <!-- Background -->
    <div class="background">
      <div class="gradient-layer"></div>
      <div class="pattern-layer"></div>

      <!-- Floating food elements -->
      <div class="floating-elements">
        <div
          class="floating-item item-1"
          :style="{ transform: `translate(${mouseX * 0.5}px, ${mouseY * 0.5}px)` }"
        >
          <svg viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="24" fill="white" />
            <circle cx="24" cy="24" r="22" fill="#FEF3C7" />
            <g transform="translate(12, 10)">
              <circle cx="8" cy="24" r="3" fill="#D97706" />
              <circle cx="20" cy="24" r="3" fill="#D97706" />
              <path
                d="M0 4 H4 L8 18 H22 L26 8 H10"
                stroke="#D97706"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </g>
          </svg>
        </div>

        <div
          class="floating-item item-2"
          :style="{ transform: `translate(${mouseX * -0.3}px, ${mouseY * -0.3}px)` }"
        >
          <svg viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="white" />
            <circle cx="20" cy="20" r="18" fill="#FFFBEB" />
            <text
              x="20"
              y="25"
              text-anchor="middle"
              fill="#D97706"
              font-size="16"
              font-weight="bold"
            >
              ?
            </text>
          </svg>
        </div>

        <div
          class="floating-item item-3"
          :style="{ transform: `translate(${mouseX * 0.4}px, ${mouseY * -0.4}px)` }"
        >
          <svg viewBox="0 0 44 44" fill="none">
            <circle cx="22" cy="22" r="22" fill="white" />
            <circle cx="22" cy="22" r="20" fill="#FEF3C7" />
            <g transform="translate(11, 9)">
              <rect x="2" y="6" width="18" height="14" rx="3" fill="#F59E0B" />
              <path
                d="M6 6 V4 C6 2 8 0 11 0 C14 0 16 2 16 4 V6"
                stroke="#D97706"
                stroke-width="2"
                fill="none"
              />
            </g>
          </svg>
        </div>

        <div
          class="floating-item item-4"
          :style="{ transform: `translate(${mouseX * -0.6}px, ${mouseY * 0.4}px)` }"
        >
          <svg viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="white" />
            <circle cx="18" cy="18" r="16" fill="#FFFBEB" />
            <text
              x="18"
              y="23"
              text-anchor="middle"
              fill="#D97706"
              font-size="14"
              font-weight="bold"
            >
              !
            </text>
          </svg>
        </div>

        <div
          class="floating-item item-5"
          :style="{ transform: `translate(${mouseX * 0.2}px, ${mouseY * 0.6}px)` }"
        >
          <svg viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="16" fill="white" />
            <circle cx="16" cy="16" r="14" fill="#FEF3C7" />
            <text
              x="16"
              y="20"
              text-anchor="middle"
              fill="#D97706"
              font-size="12"
              font-weight="bold"
            >
              %
            </text>
          </svg>
        </div>
      </div>

      <!-- Sparkles -->
      <div class="sparkle sparkle-1"></div>
      <div class="sparkle sparkle-2"></div>
      <div class="sparkle sparkle-3"></div>
      <div class="sparkle sparkle-4"></div>
      <div class="sparkle sparkle-5"></div>
      <div class="sparkle sparkle-6"></div>
    </div>

    <!-- Content -->
    <div class="content">
      <!-- Logo -->
      <div class="logo-container">
        <img src="@/assets/logo/logo.svg" alt="GoFood" class="logo" />
      </div>

      <!-- 404 Display -->
      <div class="error-display">
        <div class="error-number">
          <span class="digit digit-4-1">4</span>
          <span class="digit digit-0">
            <svg viewBox="0 0 120 120" class="plate-icon">
              <circle cx="60" cy="60" r="55" fill="#f8fafc" stroke="#e2e8f0" stroke-width="4" />
              <circle cx="60" cy="60" r="45" fill="none" stroke="#e2e8f0" stroke-width="2" />
              <circle cx="60" cy="60" r="8" fill="#D97706" />
              <path
                d="M40 50 Q60 35 80 50"
                stroke="#D97706"
                stroke-width="3"
                fill="none"
                stroke-linecap="round"
              />
              <path
                d="M40 70 Q60 85 80 70"
                stroke="#D97706"
                stroke-width="3"
                fill="none"
                stroke-linecap="round"
              />
            </svg>
          </span>
          <span class="digit digit-4-2">4</span>
        </div>
      </div>

      <!-- Message -->
      <div class="message">
        <h1 class="title">אופס! הדף לא נמצא</h1>
        <p class="subtitle">
          נראה שהעמוד שחיפשת עבר להפסקת צהריים...
          <br />
          או שהוא פשוט לא קיים.
        </p>
      </div>

      <!-- Action Button -->
      <div class="actions">
        <RouterLink :to="{ name: ROUTES.HOME }" class="btn-home">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span>חזרה לדף הבית</span>
        </RouterLink>
      </div>

      <!-- Fun fact -->
      <div class="fun-fact">
        <span class="fun-fact-icon">💡</span>
        <span class="fun-fact-text">ידעת? 404 הוא קוד שגיאה שאומר שהדף לא נמצא בשרת</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.not-found {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  direction: rtl;
}

/* Background */
.background {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.gradient-layer {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 30% 20%, rgba(245, 158, 11, 0.15) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 80%, rgba(217, 119, 6, 0.1) 0%, transparent 50%),
    linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
}

.pattern-layer {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 1px 1px, rgba(30, 41, 59, 0.03) 1px, transparent 0);
  background-size: 40px 40px;
}

/* Floating elements */
.floating-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.floating-item {
  position: absolute;
  animation: float 6s ease-in-out infinite;
  filter: drop-shadow(0 4px 12px rgba(245, 158, 11, 0.2));
  transition: transform 0.1s ease-out;
}

.item-1 {
  width: 60px;
  height: 60px;
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}

.item-2 {
  width: 44px;
  height: 44px;
  top: 25%;
  right: 15%;
  animation-delay: -1s;
}

.item-3 {
  width: 52px;
  height: 52px;
  bottom: 30%;
  left: 8%;
  animation-delay: -2s;
}

.item-4 {
  width: 40px;
  height: 40px;
  bottom: 20%;
  right: 12%;
  animation-delay: -3s;
}

.item-5 {
  width: 36px;
  height: 36px;
  top: 60%;
  right: 25%;
  animation-delay: -4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-15px) rotate(5deg);
  }
  50% {
    transform: translateY(-8px) rotate(-3deg);
  }
  75% {
    transform: translateY(-20px) rotate(3deg);
  }
}

/* Sparkles */
.sparkle {
  position: absolute;
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
  border-radius: 50%;
  animation: sparkle 2s ease-in-out infinite;
}

.sparkle-1 {
  top: 20%;
  left: 20%;
  animation-delay: 0s;
}
.sparkle-2 {
  top: 30%;
  right: 25%;
  animation-delay: -0.3s;
}
.sparkle-3 {
  bottom: 35%;
  left: 15%;
  animation-delay: -0.6s;
}
.sparkle-4 {
  bottom: 25%;
  right: 20%;
  animation-delay: -0.9s;
}
.sparkle-5 {
  top: 50%;
  left: 30%;
  animation-delay: -1.2s;
  width: 6px;
  height: 6px;
}
.sparkle-6 {
  top: 40%;
  right: 35%;
  animation-delay: -1.5s;
  width: 6px;
  height: 6px;
}

@keyframes sparkle {
  0%,
  100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Content */
.content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-lg);
  max-width: 500px;
}

/* Logo */
.logo-container {
  margin-bottom: var(--spacing-xl);
  animation: fadeInDown 0.8s ease-out;
}

.logo {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  box-shadow:
    0 10px 40px rgba(30, 41, 59, 0.15),
    0 4px 12px rgba(245, 158, 11, 0.1);
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05) rotate(-3deg);
}

@media (min-width: 768px) {
  .logo {
    width: 100px;
    height: 100px;
    border-radius: 24px;
  }
}

/* Error display */
.error-display {
  margin-bottom: var(--spacing-lg);
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.error-number {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
}

.digit {
  font-size: 100px;
  font-weight: 900;
  line-height: 1;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@media (min-width: 768px) {
  .digit {
    font-size: 140px;
  }
}

.digit-4-1 {
  animation: bounce 2s ease-in-out infinite;
}

.digit-4-2 {
  animation: bounce 2s ease-in-out infinite 0.3s;
}

.digit-0 {
  display: flex;
  align-items: center;
  justify-content: center;
}

.plate-icon {
  width: 90px;
  height: 90px;
  animation:
    spin-slow 8s linear infinite,
    wobble 2s ease-in-out infinite;
}

@media (min-width: 768px) {
  .plate-icon {
    width: 120px;
    height: 120px;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes wobble {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(3deg) scale(1.02);
  }
  75% {
    transform: rotate(-3deg) scale(0.98);
  }
}

/* Message */
.message {
  margin-bottom: var(--spacing-xl);
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: #1e293b;
  margin-bottom: var(--spacing-sm);
}

@media (min-width: 768px) {
  .title {
    font-size: var(--font-size-3xl);
  }
}

.subtitle {
  font-size: var(--font-size-base);
  color: #64748b;
  line-height: 1.7;
}

@media (min-width: 768px) {
  .subtitle {
    font-size: var(--font-size-lg);
  }
}

/* Actions */
.actions {
  margin-bottom: var(--spacing-xl);
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.btn-home {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 14px 32px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow:
    0 4px 16px rgba(245, 158, 11, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-home:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow:
    0 8px 24px rgba(245, 158, 11, 0.5),
    0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-home:active {
  transform: translateY(-1px) scale(0.98);
}

.btn-home svg {
  transition: transform 0.3s ease;
}

.btn-home:hover svg {
  transform: scale(1.1);
}

@media (min-width: 768px) {
  .btn-home {
    padding: 16px 40px;
    font-size: var(--font-size-lg);
  }
}

/* Fun fact */
.fun-fact {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(245, 158, 11, 0.2);
  animation: fadeInUp 0.8s ease-out 0.8s both;
}

.fun-fact-icon {
  font-size: 20px;
}

.fun-fact-text {
  font-size: var(--font-size-xs);
  color: #64748b;
}

@media (min-width: 768px) {
  .fun-fact-text {
    font-size: var(--font-size-sm);
  }
}

/* Animations */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
